<template>
  <div class="home-container">
    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Loading your profile...</p>
    </div>

    <!-- Authenticated Dashboard -->
    <div v-else-if="isAuthenticated" class="dashboard">
      <header class="dashboard-header">
        <div class="welcome-text">
          <h1>Welcome back, {{ username }}!</h1>
          <div class="level-badge">Level {{ stats?.level || 1 }}</div>
        </div>
        <div class="header-stats">
          <div class="stat-item">
            <span class="stat-val">{{ stats?.kanaLearned || 0 }}</span>
            <span class="stat-label">Kana</span>
          </div>
          <div class="stat-item">
            <span class="stat-val">{{ stats?.kanjiLearned || 0 }}</span>
            <span class="stat-label">Kanji</span>
          </div>
        </div>
      </header>
      
      <!-- Review Cards -->
      <section class="review-section">
        <div class="review-card kana-card" :class="{ 'has-reviews': (stats?.reviewsAvailable?.kana || 0) > 0 }">
          <div class="card-content">
            <h3>Kana Reviews</h3>
            <div class="count">{{ stats?.reviewsAvailable?.kana || 0 }}</div>
            <p class="subtitle">items ready</p>
          </div>
          <router-link to="/kana" class="action-btn">
            {{ (stats?.reviewsAvailable?.kana || 0) > 0 ? 'Start Review' : 'Practice' }}
          </router-link>
        </div>
        
        <div class="review-card kanji-card" :class="{ 'has-reviews': (stats?.reviewsAvailable?.kanji || 0) > 0 }">
          <div class="card-content">
            <h3>Kanji Reviews</h3>
            <div class="count">{{ stats?.reviewsAvailable?.kanji || 0 }}</div>
             <p class="subtitle">items ready</p>
          </div>
          <router-link to="/kanji" class="action-btn">
            {{ (stats?.reviewsAvailable?.kanji || 0) > 0 ? 'Start Review' : 'Learn More' }}
          </router-link>
        </div>
      </section>
      
      <!-- SRS Distribution -->
      <section class="progress-section" v-if="stats?.srsDistribution">
        <h2>Knowledge Base</h2>
        <div class="srs-bars">
           <!-- Apprentice -->
           <div class="srs-group">
             <div class="bar-container">
                <div class="bar apprentice" :style="{ height: getBarHeight(stats.srsDistribution.apprentice) }"></div>
             </div>
             <label>Apprentice</label>
             <span class="val">{{ stats.srsDistribution.apprentice }}</span>
           </div>
           <!-- Guru -->
           <div class="srs-group">
             <div class="bar-container">
                <div class="bar guru" :style="{ height: getBarHeight(stats.srsDistribution.guru) }"></div>
             </div>
             <label>Guru</label>
             <span class="val">{{ stats.srsDistribution.guru }}</span>
           </div>
           <!-- Master -->
           <div class="srs-group">
             <div class="bar-container">
                <div class="bar master" :style="{ height: getBarHeight(stats.srsDistribution.master) }"></div>
             </div>
             <label>Master</label>
             <span class="val">{{ stats.srsDistribution.master }}</span>
           </div>
           <!-- Enlightened -->
           <div class="srs-group">
             <div class="bar-container">
                <div class="bar enlightened" :style="{ height: getBarHeight(stats.srsDistribution.enlightened) }"></div>
             </div>
             <label>Enlightened</label>
             <span class="val">{{ stats.srsDistribution.enlightened }}</span>
           </div>
           <!-- Burned -->
           <div class="srs-group">
             <div class="bar-container">
                <div class="bar burned" :style="{ height: getBarHeight(stats.srsDistribution.burned) }"></div>
             </div>
             <label>Burned</label>
             <span class="val">{{ stats.srsDistribution.burned }}</span>
           </div>
        </div>
      </section>
    </div>

    <!-- Public Landing Page -->
    <div v-else class="landing">
      <div class="hero">
        <div class="hero-content">
          <h1>Master Japanese</h1>
          <p>Learn Hiragana, Katakana, and Kanji thoroughly with our Spaced Repetition System.</p>
          <div class="cta-buttons">
            <router-link to="/login" class="btn primary">Start Learning</router-link>
            <router-link to="/kana" class="btn secondary">Try Kana Quiz</router-link>
          </div>
        </div>
      </div>
      
      <div class="features">
        <div class="feature">
          <h3>🧠 Spaced Repetition</h3>
          <p>Review items at optimal intervals to maximize retention.</p>
        </div>
        <div class="feature">
          <h3>📊 Progress Tracking</h3>
          <p>Visualize your journey from Apprentice to Burned.</p>
        </div>
        <div class="feature">
          <h3>🇯🇵 Complete System</h3>
          <p>From basic Kana to advanced Kanji.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useAuth } from '@/composables/useAuth';
import { useApi } from '@/composables/useApi';

const { isAuthenticated, username } = useAuth();
const { get } = useApi();

const stats = ref(null);
const loading = ref(false);

const getBarHeight = (count) => {
  if (!stats.value || !stats.value.srsDistribution) return '0%';
  const max = Math.max(
    ...Object.values(stats.value.srsDistribution), 
    10 // Minimum scale
  );
  return `${Math.min((count / max) * 100, 100)}%`;
};

const fetchStats = async () => {
  if (!isAuthenticated.value) return;
  
  loading.value = true;
  try {
    const res = await get('/progress/stats');
    if (res.success) {
      stats.value = res.data;
    }
  } catch (err) {
    console.error('Failed to fetch stats', err);
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  if (isAuthenticated.value) {
    fetchStats();
  }
});
</script>

<style scoped>
.home-container {
  max-width: 1000px;
  margin: 0 auto;
}

/* Loading */
.loading-state {
  text-align: center;
  padding: 4rem;
  color: #666;
}

/* Dashboard */
.dashboard-header {
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.05);
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.welcome-text h1 {
  margin: 0;
  font-size: 1.8rem;
  color: #2a3a4a;
}

.level-badge {
  display: inline-block;
  background: #fbbf24;
  color: #2a3a4a;
  padding: 0.25rem 0.8rem;
  border-radius: 20px;
  font-weight: bold;
  font-size: 0.9rem;
  margin-top: 0.5rem;
}

.header-stats {
  display: flex;
  gap: 2rem;
}

.stat-item {
  text-align: center;
}

.stat-val {
  display: block;
  font-size: 1.5rem;
  font-weight: bold;
  color: #2a3a4a;
}

.stat-label {
  font-size: 0.8rem;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Review Cards */
.review-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.review-card {
  background: #fff;
  padding: 2rem;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 4px 6px rgba(0,0,0,0.05);
  transition: transform 0.2s, box-shadow 0.2s;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 200px;
}

.review-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 15px rgba(0,0,0,0.1);
}

.review-card h3 {
  margin: 0 0 1rem;
  color: #666;
  font-weight: normal;
}

.review-card .count {
  font-size: 3rem;
  font-weight: bold;
  color: #e5e7eb; /* Default gray for 0 */
}

.review-card .subtitle {
  margin: 0 0 1.5rem;
  color: #9ca3af;
  font-size: 0.9rem;
}

.review-card.has-reviews .count {
  color: #2a3a4a;
}

.kana-card.has-reviews .count { color: #f472b6; }
.kanji-card.has-reviews .count { color: #60a5fa; }

.action-btn {
  display: inline-block;
  padding: 0.8rem 2rem;
  background: #e5e7eb; /* Disabled-looking state */
  color: #4b5563;
  border-radius: 30px;
  text-decoration: none;
  font-weight: bold;
  transition: background 0.2s;
}

.kana-card.has-reviews .action-btn { background: #f472b6; color: white; }
.kanji-card.has-reviews .action-btn { background: #60a5fa; color: white; }

.kana-card .action-btn:hover { opacity: 0.9; }
.kanji-card .action-btn:hover { opacity: 0.9; }

/* Progress Section */
.progress-section {
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.05);
}

.progress-section h2 {
  margin-top: 0;
  margin-bottom: 2rem;
  color: #2a3a4a;
  font-size: 1.2rem;
}

.srs-bars {
  display: flex;
  justify-content: space-around;
  align-items: flex-end;
  height: 200px;
  margin-bottom: 1rem;
}

.srs-group {
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  width: 15%;
}

.bar-container {
  flex-grow: 1;
  width: 100%;
  display: flex;
  align-items: flex-end; /* Align bars to bottom */
  justify-content: center;
  margin-bottom: 0.5rem;
  background: #f3f4f6; /* Track background */
  border-radius: 8px;
  overflow: hidden;
  position: relative;
}

.bar {
  width: 100%;
  border-radius: 8px 8px 0 0;
  transition: height 1s ease-out;
  min-height: 4px; /* Visibility even if 0 */
}

/* SRS Colors (WaniKani Styleish) */
.apprentice { background: #dd5796; }
.guru { background: #882d9e; }
.master { background: #294ddb; }
.enlightened { background: #0093dd; }
.burned { background: #434343; }

.srs-group label {
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 0.2rem;
}

.srs-group .val {
  font-weight: bold;
  color: #2a3a4a;
}

/* Landing Page */
.landing {
  text-align: center;
  padding: 2rem 0;
}
.hero {
  background: linear-gradient(135deg, #2a3a4a 0%, #1e293b 100%);
  color: white;
  padding: 4rem 2rem;
  border-radius: 12px;
  margin-bottom: 3rem;
  box-shadow: 0 10px 25px rgba(0,0,0,0.1);
}
.hero h1 { margin: 0 0 1rem; font-size: 2.5rem; }
.hero p { opacity: 0.9; margin-bottom: 2rem; font-size: 1.2rem; }

.cta-buttons { display: flex; gap: 1rem; justify-content: center; }
.btn {
  padding: 1rem 2rem;
  border-radius: 6px;
  text-decoration: none;
  font-weight: bold;
}
.primary { background: #fbbf24; color: #2a3a4a; }
.secondary { background: rgba(255,255,255,0.1); color: white; border: 1px solid rgba(255,255,255,0.2); }

.features {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
}
.feature {
  padding: 2rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.05);
}
.feature h3 { color: #2a3a4a; }
.feature p { color: #666; }

@media (max-width: 600px) {
  .review-section { grid-template-columns: 1fr; }
  .srs-bars { height: 150px; }
  .srs-group label { display: none; } 
}
</style>